/**
 * CART SYSTEM V2 - INTEGRATION LAYER
 * Coordinates all cart modules and provides unified API
 * Target: 50 lines, 4KB
 */

class CartSystemV2 {
  constructor() {
    this.modules = {
      core: null,
      api: null,
      drawer: null
    };
    
    this.isReady = false;
    this.init();
  }

  debugLog(...args) {
    try {
      if (window && window.debugCart) console.log(...args);
    } catch (_) {}
  }

  async init() {
    this.debugLog('🚀 Cart System V2 initializing...');
    
    try {
      // Wait for all modules to be available
      await this.waitForModules();
      
      // Connect modules
      this.connectModules();
      
      // Setup global API
      this.setupGlobalAPI();
      
      this.isReady = true;
      this.debugLog('✅ Cart System V2 ready');
      
      // Emit ready event
      document.dispatchEvent(new CustomEvent('cart-system-v2:ready'));
      
    } catch (error) {
      console.error('❌ Cart System V2 initialization failed:', error);
      this.fallbackToLegacy();
    }
  }

  async waitForModules() {
    const checkModules = () => {
      return new Promise((resolve) => {
        const check = () => {
          if (window.cartCoreV2 && window.cartAPIV2) {
            this.modules.core = window.cartCoreV2;
            this.modules.api = window.cartAPIV2;
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });
    };
    
    await checkModules();
  }

  connectModules() {
    // Connect core to API for data operations
    const core = this.modules.core;
    const api = this.modules.api;
    
    // Override core methods to route through API while preserving optimistic lifecycle
    const originalAddItem = core.addItem.bind(core);
    core.addItem = async (variantId, quantity = 1, properties = {}, options = {}) => {
      try {
        this.debugLog('🛒 V2: Adding item via API...', { variantId, quantity, properties, options });
        const normalizedProps = core.sanitizeProperties ? core.sanitizeProperties(properties) : properties;
        // 31 Oct 2026: the add also asks Shopify for the bag (cart-core-v2.js cartFromAddAnswer); without it the core reads as before.
        const sentSeq = core.seq;
        const sentAdds = core.addsSent = (core.addsSent || 0) + 1;
        const response = await api.addToCart({ id: variantId, quantity, properties: normalizedProps }, core.cartStateSection ? { sections: core.cartStateSection } : {});
        const addedItem = Array.isArray(response?.items) ? response.items[0] : response;
        const addedQty = parseInt(addedItem?.quantity) || quantity || 1;
        const identity = core.normalizeLineIdentity
          ? core.normalizeLineIdentity(
              // Use variant identity, never the ephemeral line key
              addedItem?.variant_id ?? addedItem?.id ?? variantId,
              core.sanitizeProperties ? core.sanitizeProperties(addedItem?.properties || normalizedProps) : (addedItem?.properties || normalizedProps)
            )
          : null;

        const cleared = core.fulfillOptimistic
          ? core.fulfillOptimistic(identity, addedQty, options.placeholderId || null, { rebuild: true })
          : [];

        const answered = core.cartFromAddAnswer ? core.cartFromAddAnswer(response, { variantId, sentSeq, sentAdds, at: response && response.__answeredAt }) : null;
        const seq = core.nextSeq();
        await core.loadCartState(seq, answered ? { cart: answered.cart, at: answered.at, reason: 'add answer' } : {});

        core.emit('cart:item-added', {
          variantId,
          quantity: addedQty,
          lineIdentity: identity,
          placeholderIds: cleared
        });

        this.debugLog('✅ V2: Item added (authoritative state refreshed)');
        return true;
      } catch (error) {
        console.error('❌ V2: Add item failed:', error);
        if (options?.placeholderId && core.resolveOptimisticItem) {
          core.resolveOptimisticItem(options.placeholderId);
        }
        core.emit('cart:error', error);
        return false;
      }
    };

    const originalUpdateQuantity = core.updateQuantity.bind(core);
    core.updateQuantity = async (key, quantity, opts = {}) => {
      try {
        this.debugLog('🔄 V2: Updating quantity via API...', { key, quantity });
        await api.changeQuantity(key, quantity);
        const seq = typeof opts.seq === 'number' ? opts.seq : core.nextSeq();
        await core.loadCartState(seq);
        this.debugLog('✅ V2: Quantity updated successfully');
        return true;
      } catch (error) {
        console.error('❌ V2: Update quantity failed:', error);
        core.emit('cart:error', error);
        return false;
      }
    };
  }

  setupGlobalAPI() {
    // Backward compatibility with existing code
    window.cartSystemV2 = this;
    
    // Legacy function compatibility
    window.updateCartCount = (count) => {
      this.modules.core.updateCartCountBubble(count);
    };
    
    // Global cart functions
    window.addToCart = (variantId, quantity = 1, properties = {}) => {
      return this.modules.core.addItem(variantId, quantity, properties);
    };
    
    window.openCartDrawer = () => {
      this.modules.core.openDrawer();
    };
    
    window.closeCartDrawer = () => {
      this.modules.core.closeDrawer();
    };
  }

  fallbackToLegacy() {
    this.debugLog('🔄 Falling back to legacy cart system...');
    window.CART_FEATURE_FLAGS.USE_CART_V2 = false;
    
    // Reload page to use legacy system
    if (confirm('Cart system needs to reload. Continue?')) {
      window.location.reload();
    }
  }

  // Public API
  getCart() {
    return this.modules.core.getState();
  }

  addItem(variantId, quantity, properties) {
    return this.modules.core.addItem(variantId, quantity, properties);
  }

  updateQuantity(key, quantity) {
    return this.modules.core.updateQuantity(key, quantity);
  }

  removeItem(key) {
    return this.modules.core.removeItem(key);
  }

  openDrawer() {
    this.modules.core.openDrawer();
  }

  closeDrawer() {
    this.modules.core.closeDrawer();
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

// Always initialize (V2 is default now)
cartSystemDebugLog('🚀 Initializing Cart System V2...');

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartSystemV2Instance = new CartSystemV2();
  });
} else {
  window.cartSystemV2Instance = new CartSystemV2();
}

function cartSystemDebugLog(...args) {
  if (window && window.debugCart) console.log(...args);
}